"use client";

import React from 'react';
import { useForm } from '@tanstack/react-form';
import { CustomerInfo } from '@/domain/entities/booking.entity';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { User, Mail, Phone, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';

interface FormStepProps {
    info: CustomerInfo;
    onUpdate: (info: CustomerInfo) => void;
    allowFlexiblePhone?: boolean;
    placeholders?: Partial<Record<keyof CustomerInfo, string>>;
}

export const FormStep: React.FC<FormStepProps> = ({
    info,
    onUpdate,
    allowFlexiblePhone = false,
    placeholders,
}) => {
    const form = useForm({
        defaultValues: {
            name: info.name,
            email: info.email,
            phone: info.phone,
            notes: info.notes ?? '',
        },
        onSubmit: async ({ value }) => {
            onUpdate({
                name: value.name,
                email: value.email,
                phone: value.phone,
                notes: value.notes,
            });
        },
    });

    const handleFieldChange = (field: keyof CustomerInfo, value: string) => {
        onUpdate({ ...info, [field]: value });
    };

    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.08 }
        }
    };

    const item = {
        hidden: { opacity: 0, x: -10 },
        show: { opacity: 1, x: 0 }
    };

    return (
        <div className="max-w-2xl mx-auto">
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    form.handleSubmit();
                }}
            >
                <motion.div
                    variants={container}
                    initial="hidden"
                    animate="show"
                    className="space-y-8"
                >
                    {/* Full Name */}
                    <form.Field
                        name="name"
                        validators={{
                            onBlur: ({ value }) => {
                                if (!value || value.trim().length === 0) return 'Full name is required';
                                if (value.trim().length < 3) return 'Name must be at least 3 characters';
                                return undefined;
                            },
                        }}
                    >
                        {(field) => (
                            <motion.div variants={item} className="space-y-3">
                                <Label
                                    htmlFor="name"
                                    className="text-[10px] uppercase tracking-[0.2em] font-medium text-muted-foreground/80 ml-1"
                                >
                                    Full Name <span className="text-destructive">*</span>
                                </Label>
                                <div className="relative group">
                                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40 group-focus-within:text-primary transition-colors" />
                                    <Input
                                        id="name"
                                        name={field.name}
                                        placeholder={placeholders?.name || "e.g. Adam Smith"}
                                        value={field.state.value}
                                        onBlur={field.handleBlur}
                                        onChange={(e) => {
                                            field.handleChange(e.target.value);
                                            handleFieldChange('name', e.target.value);
                                        }}
                                        className={`pl-12 h-14 rounded-2xl border-border bg-muted/20 focus-visible:ring-primary focus-visible:bg-card transition-all ${field.state.meta.errors.length > 0 ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                    />
                                </div>
                                {field.state.meta.isTouched && field.state.meta.errors.length > 0 && (
                                    <p className="text-xs text-destructive ml-1 mt-1">
                                        {field.state.meta.errors[0]}
                                    </p>
                                )}
                            </motion.div>
                        )}
                    </form.Field>

                    {/* Email & Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        {/* Email */}
                        <form.Field
                            name="email"
                            validators={{
                                onBlur: ({ value }) => {
                                    if (!value || value.trim().length === 0) return 'Email address is required';
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                    if (!emailRegex.test(value.trim())) return 'Please enter a valid email address';
                                    return undefined;
                                },
                            }}
                        >
                            {(field) => (
                                <motion.div variants={item} className="space-y-3">
                                    <Label
                                        htmlFor="email"
                                        className="text-[10px] uppercase tracking-[0.2em] font-medium text-muted-foreground/80 ml-1"
                                    >
                                        Email Address <span className="text-destructive">*</span>
                                    </Label>
                                    <div className="relative group">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40 group-focus-within:text-primary transition-colors" />
                                        <Input
                                            id="email"
                                            name={field.name}
                                            type="email"
                                            placeholder={placeholders?.email || "name@example.com"}
                                            value={field.state.value}
                                            onBlur={field.handleBlur}
                                            onChange={(e) => {
                                                field.handleChange(e.target.value);
                                                handleFieldChange('email', e.target.value);
                                            }}
                                            className={`pl-12 h-14 rounded-2xl border-border bg-muted/20 focus-visible:ring-primary focus-visible:bg-card transition-all ${field.state.meta.errors.length > 0 ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                        />
                                    </div>
                                    {field.state.meta.isTouched && field.state.meta.errors.length > 0 && (
                                        <p className="text-xs text-destructive ml-1 mt-1">
                                            {field.state.meta.errors[0]}
                                        </p>
                                    )}
                                </motion.div>
                            )}
                        </form.Field>

                        {/* Phone */}
                        <form.Field
                            name="phone"
                            validators={{
                                onBlur: ({ value }) => {
                                    if (!value || value.trim().length === 0) return 'Phone number is required';
                                    if (allowFlexiblePhone) {
                                        const normalizedValue = value.replace(/\s+/g, '');
                                        if (!/^[+\d][\d-]{7,}$/.test(normalizedValue)) {
                                            return 'Please enter a valid phone number';
                                        }
                                        return undefined;
                                    }
                                    if (!/^\d+$/.test(value.trim())) return 'Phone number must contain numbers only';
                                    return undefined;
                                },
                            }}
                        >
                            {(field) => (
                                <motion.div variants={item} className="space-y-3">
                                    <Label
                                        htmlFor="phone"
                                        className="text-[10px] uppercase tracking-[0.2em] font-medium text-muted-foreground/80 ml-1"
                                    >
                                        Phone Number <span className="text-destructive">*</span>
                                    </Label>
                                    <div className="relative group">
                                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40 group-focus-within:text-primary transition-colors" />
                                        <Input
                                            id="phone"
                                            name={field.name}
                                            placeholder={placeholders?.phone || "0123456789"}
                                            value={field.state.value}
                                            onBlur={field.handleBlur}
                                            onChange={(e) => {
                                                const nextValue = allowFlexiblePhone
                                                    ? e.target.value
                                                    : e.target.value.replace(/\D/g, '');
                                                field.handleChange(nextValue);
                                                handleFieldChange('phone', nextValue);
                                            }}
                                            inputMode={allowFlexiblePhone ? "tel" : "numeric"}
                                            className={`pl-12 h-14 rounded-2xl border-border bg-muted/20 focus-visible:ring-primary focus-visible:bg-card transition-all ${field.state.meta.errors.length > 0 ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                        />
                                    </div>
                                    {field.state.meta.isTouched && field.state.meta.errors.length > 0 && (
                                        <p className="text-xs text-destructive ml-1 mt-1">
                                            {field.state.meta.errors[0]}
                                        </p>
                                    )}
                                </motion.div>
                            )}
                        </form.Field>
                    </div>

                    {/* Notes (optional) */}
                    <form.Field name="notes">
                        {(field) => (
                            <motion.div variants={item} className="space-y-3">
                                <Label
                                    htmlFor="notes"
                                    className="text-[10px] uppercase tracking-[0.2em] font-medium text-muted-foreground/80 ml-1"
                                >
                                    Special Notes <span className="text-muted-foreground/50">(Optional)</span>
                                </Label>
                                <div className="relative group">
                                    <MessageSquare className="absolute left-4 top-5 w-4 h-4 text-muted-foreground/40 group-focus-within:text-primary transition-colors" />
                                    <Textarea
                                        id="notes"
                                        name={field.name}
                                        placeholder={placeholders?.notes || "Tell us about any specific details or special requests..."}
                                        value={field.state.value}
                                        onBlur={field.handleBlur}
                                        onChange={(e) => {
                                            field.handleChange(e.target.value);
                                            handleFieldChange('notes', e.target.value);
                                        }}
                                        className="pl-12 min-h-[160px] rounded-2xl border-border bg-muted/20 focus-visible:ring-primary focus-visible:bg-card pt-5 transition-all resize-none"
                                    />
                                </div>
                            </motion.div>
                        )}
                    </form.Field>
                </motion.div>
            </form>
        </div>
    );
};
